import { useState, useEffect, useMemo } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import TemplateCard from '../components/template-card';
import PagePreview from './page-preview';
import SearchBar from '../components/search-bar';
import SkeletonLoader from '../components/skeleton-loader';
import KitCard from '../components/kit-card';
import useInsertPattern from '../hooks/use-insert-pattern';
import { highlightText } from '../utils/highlight';
import CustomSelect from '../components/custom-select';
import { sortTemplates } from '../utils/sort-templates';
import { SORT_OPTIONS } from '../constants/pages';
import { loadFavorites, handleToggleFavorite, getCachedFavorites } from '../utils/favorites-service';

const FavoritesPage = ({ onInsertComplete, isProActive, handleUpgradeClick }) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [filterType, setFilterType] = useState('all');
	const [sortBy, setSortBy] = useState('default');
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [activePage, setActivePage] = useState(null);
	const [error, setError] = useState(null);

	const insertPattern = useInsertPattern(onInsertComplete);

	useEffect(() => {
		const cachedTemplates = getCachedFavorites();

		if (cachedTemplates.length) {
			setTemplates(cachedTemplates);
			setLoading(false);
		}

		loadFavorites({
			setTemplates,
			setLoading: cachedTemplates.length ? () => {} : setLoading,
			setError,
		});
	}, []);

	const toggleFavoriteHandler = (id) => handleToggleFavorite(id, setTemplates);

	const filtered = useMemo(() => {
		return templates
			.filter((item) => (filterType === 'all' ? true : item.type === filterType))
			.filter((item) => item.title.toLowerCase().includes(searchQuery.toLowerCase()));
	}, [templates, filterType, searchQuery]);

	const sorted = useMemo(() => {
		return filtered.length > 1 ? sortTemplates(filtered, sortBy) : filtered;
	}, [filtered, sortBy]);

	return (
		<div className="vb-content-favouirtes-page">
			{/* Toolbar */}
			{!activePage && (
				<div className="vayu-page-toolbar vayu-favorites-toolbar">
					<div className="vayu-toolbar-right">
						{sorted.length > 1 && (
							<CustomSelect
								value={sortBy}
								onChange={setSortBy}
								options={SORT_OPTIONS}
							/>
						)}

						<CustomSelect
							value={filterType}
							onChange={setFilterType}
							options={[
								{ label: 'All', value: 'all' },
								{ label: 'Patterns', value: 'patterns' },
								{ label: 'Pages', value: 'pages' },
								{ label: 'Kits', value: 'kits' },
							]}
							placeholder="Filter by type"
							className="vayu-select-theme"
							width="180px"
						/>

						<SearchBar
							onSearch={setSearchQuery}
							activeTab="favorites"
							className="vayu-search-bar"
						/>
					</div>
				</div>
			)}

			{/* Main Content */}
			<section className="vayu-main-content">
				{activePage ? (
					<PagePreview
						kit={activePage}
						onBack={() => setActivePage(null)}
						onInsertComplete={onInsertComplete}
						handleUpgradeClick={handleUpgradeClick}
					/>
				) : loading ? (
					<SkeletonLoader count={6} />
				) : error ? (
					<p className="vayu-error-state">{error}</p>
				) : sorted.length > 0 ? (
					<div className={`vayu-template-grid ${templates.length > 0 ? 'fade-in-ready' : ''}`}>
						{sorted.map((tpl, index) => {
							const commonProps = {
								key: tpl.id,
								title: (
									<span
										dangerouslySetInnerHTML={{
											__html: highlightText(tpl.title, searchQuery),
										}}
									/>
								),
								style: { '--index': index },
								preview: tpl.preview,
								isFavorite: true,
								onToggleFavorite: () => toggleFavoriteHandler(tpl.id),
							};

							if (tpl.type === 'patterns') {
								return (
									<TemplateCard
										{...commonProps}
										pattern={tpl.pattern || ''}
										premium={tpl?.premium}
										onClick={() => insertPattern(tpl.pattern, tpl.premium)}
										isProActive={isProActive}
										handleUpgradeClick={handleUpgradeClick}
									/>
								);
							} else {
								return (
									<KitCard
										{...commonProps}
										pattern={tpl.pattern || ''}
										templateCount={tpl.templates?.length || 0}
										onClick={() => setActivePage(tpl)}
										premium={tpl?.premium}
										isProActive={isProActive}
										handleUpgradeClick={handleUpgradeClick}
									/>
								);
							}
						})}
					</div>
				) : (
					<p className={`vayu-empty-state ${searchQuery ? 'has-query' : ''}`}>
						{searchQuery ? (
							<>
								<span className="vayu-empty-state__icon">🔍</span>
								{__('No favourites found for', 'vayu-blocks')}
								<span className="vayu-empty-state__highlight">"{searchQuery}"</span>
							</>
						) : (
							<>
								<span className="vayu-empty-state__icon">📁</span>
								{__('No templates available here yet.', 'vayu-blocks')}
							</>
						)}
					</p>
				)}
			</section>
		</div>
	);
};

export default FavoritesPage;
